import { getCurrenciesExchangeRate } from 'api/currency';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useLocalStorage, useSessionStorage } from 'react-use';
import { getDefaultCurrencyForCountry } from 'utils/functions';

// Context
import CurrencyContext from 'context/CurrencyContext';

// Constants
import currencies from 'constants/currencies';
import { DEFAULT_COOKIE_OPTIONS } from 'utils/cookies';

const COOKIE_NAME = 'creoate_current_currency';
const DEFAULT_CURRENCY = 'GBP';

const CurrencyProvider = ({ children, userCountry }) => {
  // own state
  const [currency, setLocalCurrency] = useLocalStorage(
    'currency',
    currencies.find((c) => c.name === DEFAULT_CURRENCY)
  );

  const [exchangeRates, setExchangeRates] = useSessionStorage('exchangeRates');
  const [cookies, setCookie] = useCookies([COOKIE_NAME]);

  // Set currency based on user's shipping country only if no user choice exists
  useEffect(() => {
    const shouldSetDefaultCurrency = userCountry && !cookies[COOKIE_NAME];
    if (!shouldSetDefaultCurrency) return;

    const defaultCurrency = getDefaultCurrencyForCountry(userCountry);
    const currencyObj = currencies.find((c) => c.name === defaultCurrency);
    if (!currencyObj) return;

    setLocalCurrency(currencyObj);
  }, [userCountry, cookies[COOKIE_NAME], setLocalCurrency]);

  // fetch exchange rates from external source
  useEffect(() => {
    if (!exchangeRates) {
      (async () => {
        setExchangeRates(
          (await getCurrenciesExchangeRate()).reduce(
            (prevValue, currValue) => ({
              ...prevValue,
              [`${currValue.tradingPair}`]: currValue.rate,
            }),
            {}
          )
        );
      })();
    }
  }, [exchangeRates, setExchangeRates]);

  // actions
  const setCurrency = useCallback(
    (newCurrencyName) => {
      setLocalCurrency(currencies.find((c) => c.name === newCurrencyName));
      setCookie(COOKIE_NAME, newCurrencyName, DEFAULT_COOKIE_OPTIONS);
    },
    [setCookie, setLocalCurrency]
  );

  // currency state
  const state = useMemo(
    () => ({
      currency,
      exchangeRates,
      setCurrency,
    }),
    [exchangeRates, currency, setCurrency]
  );

  return <CurrencyContext.Provider value={state}>{children}</CurrencyContext.Provider>;
};

CurrencyProvider.propTypes = {
  children: PropTypes.node.isRequired,
  userCountry: PropTypes.string,
};

export default CurrencyProvider;
